import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { toast } from 'react-hot-toast'
import { CheckCircle, Clock, XCircle, FileText, Shield, UserCheck, RefreshCw } from 'lucide-react'
import AuthHeader from '../../components/company/registration/AuthHeader'
import StepIndicator from '../../components/company/registration/StepIndicator'
import DocumentUpload from './steps/DocumentUpload'
import { companyService } from '../../services/companyService'
import { authService } from '../../services/authService'
import { companyRegistrationSteps } from '../../utils/companyRegistrationSteps'

const DocumentUploadPage = () => {
    const navigate = useNavigate()

    // State
    const [isLoading, setIsLoading] = useState(false)
    const [userId, setUserId] = useState(null)
    const [companyId, setCompanyId] = useState(null)
    const [bgvStatus, setBgvStatus] = useState('pending') // pending, uploaded, verification_failed, verified
    const [adminStatus, setAdminStatus] = useState('pending') // pending, approved, rejected
    const [documents, setDocuments] = useState({})
    const [rejectionReasons, setRejectionReasons] = useState([])

    const intervalRef = useRef(null)

    // Current Step: 4
    const currentStepConfig = companyRegistrationSteps.find(s => s.id === 4)

    // Fetch Status
    const fetchStatus = async () => {
        try {
            const user = await authService.getCurrentUser()
            if (!user) return

            // Use user data directly (avoid calling company profile APIs)
            // Accept multiple possible field names returned by the API
            // Examples from API: user_id, company_id, BGV_status, bgv_reason, admin_review
            const bgvStatusFromUser = user.BGV_status || user.bgv_status || user.bgvStatus || user.Bgv_status
            const adminStatusFromUser = user.admin_review || user.adminReview || user.admin_review_status || user.adminReviewStatus

            setBgvStatus(bgvStatusFromUser || 'pending')
            setAdminStatus(adminStatusFromUser || 'pending')

            // Documents: may be missing in the user payload
            if (user.documents) setDocuments(user.documents)

            // Rejection reasons: API may return a single reason string (bgv_reason) or an array
            if (Array.isArray(user.rejection_reasons)) {
                setRejectionReasons(user.rejection_reasons)
            } else if (user.rejection_reasons) {
                setRejectionReasons([user.rejection_reasons])
            } else if (user.bgv_reason) {
                setRejectionReasons([user.bgv_reason])
            } else if (user.bgvReasons) {
                setRejectionReasons(Array.isArray(user.bgvReasons) ? user.bgvReasons : [user.bgvReasons])
            }
        } catch (e) {
            console.error('Error fetching status', e)
        }
    }

    // Initial Load & Auth Check
    useEffect(() => {
        const init = async () => {
            const user = await authService.getCurrentUser()
            if (!user) {
                navigate('/auth/login')
                return
            }
            // Prefer user object values (some responses use user_id/company_id)
            setUserId(user.id || user.user_id)
            setCompanyId(sessionStorage.getItem('companyId') || user.company_id || user.companyId)

            // Check if previous steps done?
            // Prompt says: If false -> Step 1...
            // user.hasCompletedRegistration should be true (from Step 3)
            // or "check logic"

            await fetchStatus()
        }
        init()
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [navigate])

    // Polling logic
    // Poll if in progress (uploaded) to check for verification
    useEffect(() => {
        const shouldPoll = bgvStatus === 'uploaded' || bgvStatus === 'in_progress'

        if (shouldPoll && !intervalRef.current) {
            intervalRef.current = setInterval(fetchStatus, 30000)
        } else if (!shouldPoll && intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [bgvStatus])

    // Auto Redirect
    useEffect(() => {
        if (adminStatus === 'approved') { // Removed bgvStatus === 'verified' constraint per prompt "once status of admin review approved"
            toast.success('Account Approved! Redirecting to Dashboard...')
            setTimeout(() => {
                navigate('/dashboard/company')
            }, 2000)
        }
    }, [adminStatus, navigate])


    const isVerificationScreen = useMemo(() => {
        return bgvStatus === 'uploaded' || bgvStatus === 'in_progress' || bgvStatus === 'verified'
    }, [bgvStatus])


    const handleUploadSubmit = async (uploadData) => {
        try {
            setIsLoading(true)

            // Upload documents
            // uploadData.documents is object { key: File, ... }
            // companyService.uploadDocument expects formData

            // We need to upload each file one by one or all?
            // Existing DocumentUpload component seems to return { documents: { key: File } }
            // companyService.uploadDocument(formData)

            // Call server to mark documents as submitted for review
            try {
                const response = await companyService.updateUserBGVStatus('uploaded')
                if (response && response.success) {
                    setBgvStatus('uploaded')
                    toast.success(response.message || 'Documents submitted for review')
                } else {
                    // Some APIs return success flag differently
                    setBgvStatus('uploaded')
                    toast.success('Documents submitted for review')
                }
            } catch (err) {
                console.error('Failed to submit documents for review', err)
                toast.error(err?.message || 'Failed to submit documents for review')
            }

        } catch (error) {
            console.error(error)
            toast.error('Upload failed')
        } finally {
            setIsLoading(false)
        }
    }

    // Status Steps Component
    const StatusSteps = () => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-xl border ${bgvStatus !== 'pending' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center space-x-3 mb-2">
                    <div className={`p-2 rounded-full ${bgvStatus !== 'pending' ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <FileText className={`w-4 h-4 ${bgvStatus !== 'pending' ? 'text-green-600' : 'text-gray-500'}`} />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Upload</h3>
                </div>
                <div className="mt-2 flex items-center text-xs font-medium">
                    {bgvStatus !== 'pending' ? (
                        <span className="text-green-700 flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> submitted</span>
                    ) : <span className="text-gray-500">Pending</span>}
                </div>
            </div>

            <div className={`p-4 rounded-xl border ${bgvStatus === 'verified' ? 'border-green-200 bg-green-50' :
                    bgvStatus === 'verification_failed' || rejectionReasons.length > 0 ? 'border-red-200 bg-red-50' :
                        bgvStatus === 'uploaded' ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                }`}>
                <div className="flex items-center space-x-3 mb-2">
                    <div className={`p-2 rounded-full ${bgvStatus === 'verified' ? 'bg-green-100' :
                            bgvStatus === 'verification_failed' ? 'bg-red-100' :
                                bgvStatus === 'uploaded' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                        <Shield className={`w-4 h-4 ${bgvStatus === 'verified' ? 'text-green-600' :
                                bgvStatus === 'verification_failed' ? 'text-red-600' :
                                    bgvStatus === 'uploaded' ? 'text-blue-600' : 'text-gray-500'
                            }`} />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Verification</h3>
                </div>
                <div className="mt-2 flex items-center text-xs font-medium">
                    {bgvStatus === 'verified' ? (
                        <span className="text-green-700 flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Verified</span>
                    ) : bgvStatus === 'verification_failed' || rejectionReasons.length > 0 ? (
                        <span className="text-red-700 flex items-center"><XCircle className="w-3 h-3 mr-1" /> Action Required</span>
                    ) : bgvStatus === 'uploaded' ? (
                        <span className="text-blue-700 flex items-center"><Clock className="w-3 h-3 mr-1 animate-pulse" /> In Progress</span>
                    ) : <span className="text-gray-500">Pending</span>}
                </div>
            </div>

            <div className={`p-4 rounded-xl border ${adminStatus === 'approved' ? 'border-green-200 bg-green-50' :
                    adminStatus === 'rejected' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
                }`}>
                <div className="flex items-center space-x-3 mb-2">
                    <div className={`p-2 rounded-full ${adminStatus === 'approved' ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <UserCheck className={`w-4 h-4 ${adminStatus === 'approved' ? 'text-green-600' : 'text-gray-500'}`} />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Admin Review</h3>
                </div>
                <div className="mt-2 flex items-center text-xs font-medium">
                    {adminStatus === 'approved' ? (
                        <span className="text-green-700 flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Approved</span>
                    ) : adminStatus === 'rejected' ? (
                        <span className="text-red-700 flex items-center"><XCircle className="w-3 h-3 mr-1" /> Rejected</span>
                    ) : <span className="text-gray-500 flex items-center"><Clock className="w-3 h-3 mr-1" /> Pending</span>}
                </div>
            </div>
        </div>
    )

    return (
        <div className="min-h-screen bg-gray-50">
            <AuthHeader />

            <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-8 gap-6">
                <div className="w-full max-w-6xl">
                    <StepIndicator currentStep={4} totalSteps={companyRegistrationSteps.length} />
                </div>

                <div className="w-full max-w-6xl">
                    <StatusSteps />

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8"
                    >
                        {/* Header tied to state usually? Or just Status vs Upload title */}
                        {isVerificationScreen ? (
                            <>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Application Status</h2>
                                <StatusSteps />

                                <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-6">
                                    <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
                                    <p className="text-blue-700 mb-4">
                                        Our team is reviewing your documents. This usually takes 24-48 hours.
                                        You will be notified via email once the process is complete.
                                        This page will automatically update when your status changes.
                                    </p>
                                    <button
                                        onClick={fetchStatus}
                                        className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-1" /> Check Status Now
                                    </button>
                                </div>

                                {/* Rejection Handling */}
                                {(rejectionReasons.length > 0 || adminStatus === 'rejected') && (
                                    <div className="mt-8">
                                        <div className="bg-red-50 border border-red-100 rounded-xl p-6 mb-6">
                                            <h3 className="font-semibold text-red-900 mb-2 flex items-center">
                                                <XCircle className="w-5 h-5 mr-2" /> Action Required
                                            </h3>
                                            <p className="text-red-700 mb-4">
                                                Some documents were rejected. Please review the reasons below and re-upload the corrected documents.
                                            </p>
                                            <ul className="list-disc list-inside text-red-600 text-sm mb-4">
                                                {rejectionReasons.map((r, i) => <li key={i}>{r}</li>)}
                                            </ul>
                                            <button
                                                onClick={() => setBgvStatus('pending')}
                                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                            >
                                                Re-upload Documents
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Step Header inside Card for Upload */}
                                <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
                                    <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
                                        {currentStepConfig?.title || 'Documents'}
                                    </h1>
                                    <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
                                        {currentStepConfig?.description || 'Upload legal documents'}
                                    </p>
                                </div>

                                <DocumentUpload
                                    data={{ documents }}
                                    onNext={handleUploadSubmit}
                                    onPrevious={() => navigate('/company/registration/subscription')}
                                    isLoading={isLoading}
                                    userId={userId}
                                    companyId={companyId}
                                />
                            </>
                        )}
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default DocumentUploadPage

